import React from 'react';
import _ = require('underscore');

import { ButtonType } from '@tableau/widgets';

import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { CheckboxState } from '../../common/components/checkboxes/CheckboxState';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { AngularContext, AngularContextFactory } from '../../ts/factories/AngularContextFactory';
import { ListResult } from '../../ts/fetchers/ListResult';
import { L10n } from '../../ts/L10n/L10n';
import { IServerUserInfo } from '../../ts/models/IServerUserInfo';
import { Set } from '../../ts/models/Set';
import { IGetUsersSiteRolesResult } from '../../ts/ServerApi/requests/GetUsersSiteRolesRequest';
import { IUpdateServerUsersSiteMembershipResult } from '../../ts/ServerApi/requests/UpdateServerUsersSiteMembershipRequest';
import { IServerUser } from '../../ts/ServerApi/types/IServerUser';
import { ISiteRole, SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { IUserRole } from '../../ts/ServerApi/types/IUserRole';
import { SiteMembershipUpdateActionNotification } from '../services/notifications/SiteMembershipUpdateActionNotification';
import { ServerUsers } from '../services/resources/ServerUsers';
import { SiteRolesResource } from '../services/resources/SiteRoles';
import { IUsersWithSiteRolesModel, SiteRolesPickerClientService } from '../services/SiteRolesPickerClientService';
import { ISiteRoleChanges, IUserCountsAndRolesBySiteId, SiteRolesPickerContainer } from './SiteRolesPickerContainer';

export interface EditSiteMembershipDialogState {
  readonly busy: boolean;
  readonly siteRoleChanges: ISiteRoleChanges;
  readonly userRoleSelections?: UserRoleSelections;
}

export interface EditSiteMembershipDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  readonly users: Set<IServerUserInfo>;
}

export interface UserRoleSelections {
  readonly userCountsAndRolesBySiteId: IUserCountsAndRolesBySiteId;
  readonly numSelectedUsers: number;
  readonly numSelectedServerAdmins: number;
}

export class EditSiteMembershipDialog extends React.Component<EditSiteMembershipDialogProps, EditSiteMembershipDialogState> {
  private dialogScope: ng.IScope = ServiceLocator.getAngularActionContext().scope;
  private angularContext: AngularContext = ServiceLocator.get<AngularContextFactory>('AngularContextFactory').create(this.dialogScope);
  private $q: ng.IQService = this.angularContext.getService('$q');
  private siteMembershipUpdateActionNotification: SiteMembershipUpdateActionNotification = SiteMembershipUpdateActionNotification.instance;
  public usersWithSiteRoles: IUsersWithSiteRolesModel;

  public constructor(props: EditSiteMembershipDialogProps) {
    super(props);
    this.state = {
      busy: false,
      userRoleSelections: undefined,
      siteRoleChanges: {
        siteRoles: {},
        serverAdmin: undefined
      }
    };
    this.getSiteRolesForSelectedUsers();
  }

  private readonly applySiteRolesCallback = () => {
    this.apply();
  }

  private readonly siteRoleChangesCallback = (siteRoleChanges: ISiteRoleChanges) => {
    this.setState({siteRoleChanges: siteRoleChanges});
  }

  public render() {
    return (
      <div className="tb-site-membership" tb-test-id="confirm-action-dialog-form">
        <div className="tb-dialog-title">
          <span>
            {Messages.siteMembership()}
          </span>
        </div>
        <div className="tb-dialog-description"> {this.getEditSiteMembershipDescription()} </div>
        <div className="tb-picker-container tb-fill">
            <SiteRolesPickerContainer
              changes={this.siteRoleChangesCallback}
              busy={this.state.busy}
              angularContext={this.angularContext}
              userRoleSelections={this.state.userRoleSelections}
            />
        </div>
        <div className="tb-dialog-actions">
          <ButtonWithSpinner
              buttonType={ButtonType.Outline}
              showSpinner={false}
              clicked={this.props.dismiss}
              testId={'confirm-action-dialog-cancel'}
              title={Messages.cancel()}
            />
            <span className="tb-lr-padded">
              <ButtonWithSpinner
                buttonType={ButtonType.Go}
                showSpinner={this.state.busy}
                clicked={this.applySiteRolesCallback}
                testId={'confirm-action-dialog-submit'}
                title={Messages.save()}
                disabled={this.state.busy || !this.isDirty()}
              />
            </span>
          </div>
        </div>
    );
  }

  private getEditSiteMembershipDescription() {
    const displayName = (this.props.users.size() > 0) ? this.props.users.first().displayName : '';
    let editSiteMembershipDescription =
    Messages.editSiteMembershipDescription(
        { USER_COUNT: this.props.users.size(), USER_NAME: L10n.elideIfLong(displayName), USER_COUNT_LOC: L10n.localizedCount(this.props.users.size()) });
    return editSiteMembershipDescription;
  }

  private apply(): ng.IPromise<any> {
    this.setState({busy: true});

    let promise: ng.IPromise<any> = this.$q.when({});

    const originalAdminState = this.adminStateFromUsers(this.getServerAdminCount(), this.props.users.size());

    if (this.state.siteRoleChanges.serverAdmin !== undefined) {
      // server admin changed
      promise = this.updateServerAdminRoles(this.props.users, this.state.siteRoleChanges);
    } else if (originalAdminState === CheckboxState.empty && !_.isEmpty(this.state.siteRoleChanges.siteRoles)) {
      // server is not checked, site roles changed
      promise = this.updateSiteMembership(this.props.users, this.state.siteRoleChanges);
    }

    // After all the requests dance, fire events and close the dialog, we're done here.
    return promise.then((result: any) => {
      this.props.close(result);
    });
  }

   // Get the original/current state of server admins of the users.
   public adminStateFromUsers(adminCount: number, totalUsersCount: number): string {
    if (adminCount === 0) {
      return CheckboxState.empty;
    } else if (adminCount < totalUsersCount) {
      return CheckboxState.mixed;
    } else {
      return CheckboxState.checked;
    }
  }

  private isDirty(): boolean {
    const originalAdminState = this.adminStateFromUsers(this.getServerAdminCount(), this.props.users.size());
    const serverAdminChanged = this.state.siteRoleChanges.serverAdmin && (this.state.siteRoleChanges.serverAdmin !== originalAdminState);
    const siteRolesChanged = (originalAdminState === CheckboxState.empty && !_.isEmpty(this.state.siteRoleChanges.siteRoles));
    return serverAdminChanged || siteRolesChanged;
  }

   // section: get filtered server users

   public getFilteredServerUsers(users: Set<IServerUserInfo>, newAdminState: CheckboxState, serverAdmins: string[]): Set<IServerUserInfo> {
    if (newAdminState) {
      // promote non admin users only
      return users.filter((user: IServerUserInfo) => !_.contains(serverAdmins, user.id));
    } else {
      // demote admin users only
      return users.filter((user: IServerUserInfo) => _.contains(serverAdmins, user.id));
    }
  }

  public getServerAdminCount() {
    return this.usersWithSiteRoles ? this.usersWithSiteRoles.serverAdmins.length : 0;
  }

  public calculateUserCountsAndRolesBySite(userRoles: IUserRole[]): IUserCountsAndRolesBySiteId {
    const userCountsAndRolesBySiteId: IUserCountsAndRolesBySiteId = {};

    _.each(userRoles, (userRole: IUserRole) => {
      _.each(userRole.sites, (site: ISiteRole) => {
        const siteUserInfo = userCountsAndRolesBySiteId[site.siteId];
        if (siteUserInfo) {
          ++siteUserInfo.count;
          if (siteUserInfo.siteRole !== site.siteRole) {
            siteUserInfo.siteRole = SiteRolesResource.Mixed;
          }
        } else {
          userCountsAndRolesBySiteId[site.siteId] = {
            count: 1,
            siteRole: site.siteRole
          };
        }
      });
    });

    return userCountsAndRolesBySiteId;
  }

  public updateServerAdminRoles(users: Set<IServerUserInfo>, siteRoleChanges: ISiteRoleChanges): ng.IPromise<any> {
    return this.updateServerAdmin(users, siteRoleChanges).then(() => {
      if (siteRoleChanges.serverAdmin === CheckboxState.empty && !_.isEmpty(siteRoleChanges.siteRoles)) {
        // server admin got unchecked, site roles changed
        return this.updateSiteMembership(users, siteRoleChanges);
      }
    });
  }
  // section: update server admin

  public updateServerAdmin(users: Set<IServerUserInfo>, siteRoleChanges: ISiteRoleChanges): ng.IPromise<any> {
    // users in the set might be stale, so ask the server for the latest admin status
    return this.getServerAdmins(users.keys()).then((serverAdmins: string[]) => {
      const newAdminState = (siteRoleChanges.serverAdmin === CheckboxState.checked);
      let filteredServerUsers: Set<IServerUserInfo> = this.getFilteredServerUsers(users, newAdminState, serverAdmins);
      return SiteRolesPickerClientService.updateServerAdmin(
        filteredServerUsers,
        newAdminState);
    });
  }

  // section: update site membership or roles

  public updateSiteMembership(users: Set<IServerUserInfo>, siteRoleChanges: ISiteRoleChanges): ng.IPromise<any> {
    const userIds = users.keys();
    const params = SiteRolesPickerClientService.siteRoleChangesToParams(userIds, siteRoleChanges.siteRoles);
    return SiteRolesPickerClientService.updateServerUsersSiteMembership(params).then((result: IUpdateServerUsersSiteMembershipResult) => {

      if(result.results || result.errors){
        // show toast
        this.siteMembershipUpdateActionNotification.notify(
            result.results,
            result.errors,
            users,
            siteRoleChanges.siteRoles);
      }
    });
  }

  public getSiteRolesForSelectedUsers(): ng.IPromise<any> {
    // return if there are no selected users.
    if (this.props.users.size() === 0) {
      return this.$q.when({});
    }

    // get site role data for the selected users.
    const userIds = this.props.users.keys();
    return ServerUsers.instance.getSiteRoles(userIds).then((result: IGetUsersSiteRolesResult) => {
      const usersModel: IUsersWithSiteRolesModel = {
        users: this.props.users,
        userRoles: result.users,
        serverAdmins: []
      };

      // now get server admin data
      return this.getServerAdmins(userIds).then((serverAdmins: string[]) => {
        // now calculate number of server admins since it is not included in getSiteRoles
        usersModel.serverAdmins = serverAdmins;
        let userCountsAndRolesBySiteId = this.calculateUserCountsAndRolesBySite(usersModel.userRoles);
        let numSelectedUsers = usersModel.users.size();
        let numSelectedServerAdmins = serverAdmins.length;

        const userRoleSelections: UserRoleSelections = {
          userCountsAndRolesBySiteId: userCountsAndRolesBySiteId,
          numSelectedUsers: numSelectedUsers,
          numSelectedServerAdmins: numSelectedServerAdmins
        };
        this.usersWithSiteRoles = usersModel;
        this.setState({userRoleSelections: userRoleSelections});
      });
    });
  }

  public getServerAdmins(userIds: string[]): ng.IPromise<string[]> {
    const serverAdmins: string[] = [];
    return ServerUsers.instance.getByUserIds(userIds).then((usersList: ListResult<IServerUser>) => {
      // now calculate number of server admins since it is not included in the results
      _.each(usersList.items, (user: IServerUser) => {
        if (user.maxSiteRole === SiteRoles.ServerAdministrator) {
          serverAdmins.push(user.id);
        }
      });
      return this.$q.when(serverAdmins);
    });
  }
}
